/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { PublicUser } from '@brandonstrong/types';
import { api, ApiClientError, setAccessToken } from './api';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  user: PublicUser | null;
  status: AuthStatus;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Public visitors never have a session, so the silent refresh (and its 401 in
 * the console) only runs under /admin or when this tab has signed in before.
 */
const ADMIN_MARKER = 'bs.admin';
const shouldRestoreSession = (): boolean => {
  if (window.location.pathname.startsWith('/admin')) return true;
  try {
    return localStorage.getItem(ADMIN_MARKER) === '1';
  } catch {
    return false;
  }
};
const setAdminMarker = (on: boolean): void => {
  try {
    if (on) localStorage.setItem(ADMIN_MARKER, '1');
    else localStorage.removeItem(ADMIN_MARKER);
  } catch {
    /* ignore */
  }
};

/**
 * Admin session state. Public visitors never authenticate; this only matters
 * under /admin. On load it tries a silent refresh with the httpOnly cookie.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  useEffect(() => {
    let cancelled = false;
    if (!shouldRestoreSession()) {
      setStatus('anonymous');
      return;
    }
    (async () => {
      try {
        const { accessToken } = await api.auth.refresh();
        setAccessToken(accessToken);
        const me = await api.auth.me();
        if (!cancelled) {
          setUser(me);
          setStatus('authenticated');
        }
      } catch (error) {
        if (!(error instanceof ApiClientError)) console.error('Session restore failed', error);
        if (!cancelled) {
          setAccessToken(null);
          setUser(null);
          setStatus('anonymous');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the short-lived access token fresh while the tab is open.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const timer = setInterval(() => {
      api.auth
        .refresh()
        .then(({ accessToken }) => setAccessToken(accessToken))
        .catch(() => {
          setAccessToken(null);
          setUser(null);
          setStatus('anonymous');
        });
    }, 10 * 60_000);
    return () => clearInterval(timer);
  }, [status]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.auth.login({ email, password });
    setAdminMarker(true);
    setAccessToken(res.accessToken);
    setUser(res.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setAdminMarker(false);
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
      isSuperAdmin: user?.role === 'super_admin',
      login,
      logout,
    }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
